<template>
  <div v-if="!booking.is_offer">
    <div class="dashboard-block">

      <h2>Request Status</h2>

      <div class="booking-progress-steps">

        <div class="alert alert-success alert-icon">
          <i class="fa fa-check-circle"></i>
          Congrats! Your {{ $t('request') }} has been submitted
        </div>

        <div class="alert alert-icon" :class="alertClassRequestIsVetted">
          <i class="fa" :class="iconClassRequestIsVetted"/>
          Your {{ $t('request') }} is being vetted by the {{ clientName }} team, we’ll be in touch shortly
        </div>

        <div class="alert alert-icon" :class="alertClassTalentReviewingRequest">
          <i class="fa" :class="iconClassTalentReviewingRequest"/>
          Talent are reviewing your {{ $t('request') }}
        </div>

        <div class="alert alert-icon" :class="alertClassTalentChosen">
          <i class="fa" :class="iconClassTalentChosen"/>
          Choose your talent from list of applicants
        </div>

      </div>
    </div>
    <div class="alert alert-danger alert-icon" v-if="bookingTypeIsBookingRequest && bookingHasExpired">
      <i class="fa fa-frown-o"></i>
      <span v-if="bookingHaveNoAppliedTalent">
        You did not have any responses in time. Feel free to change your request or contact the {{ clientName }} team for advice.
      </span>
      <span v-else>
        Unfortunately your booking has expired. Contact the {{ clientName }} team for advice.
      </span>
    </div>
  </div>
</template>

<script>
import { helpers } from 'mixins/bookings'
import { filter, get } from 'lodash'

export default {
  mixins: [helpers],

  components: {},

  props: {
    booking: {
      type: Object,
      required: true
    }
  },

  computed: {

    adminBookingStatus () {
      if (this.bookingTermsAreNotAccepted) {
        return 'Terms & Conditions not accepted'
      }

      if (this.bookingPaymentDepositIsUnpaid) {
        return 'Deposit unpaid'
      }

      if (this.bookingPaymentDepositIsUnpaid) {
        return 'Final unpaid'
      }

      return false
    },

    /**
     * Determine if the booking has no applied talent.
     *
     * @return bool
     */
    bookingHaveNoAppliedTalent () {
      return !this.bookingHaveAppliedTalent
    },

    /**
     * Determine if the booking has applied talent.
     *
     * @return bool
     */
    bookingHaveAppliedTalent () {
      return filter(this.booking.talent, talent => get(talent, 'response.status') === 'applied').length > 0
    },

    /**
     * Alert class for the request is vetted progress step.
     *
     * @return string
     */
    alertClassRequestIsVetted () {
      if (this.bookingIsVetting) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the request is vetted progress step.
     *
     * @return string
     */
    iconClassRequestIsVetted () {
      if (this.bookingIsVetting) {
        return 'fa-circle-o'
      }

      return 'fa-check-circle'
    },

    /**
     * Alert class for the talent reviewing request progress step.
     *
     * @return string
     */
    alertClassTalentReviewingRequest () {
      if (this.bookingIsVetting) {
        return 'alert-default'
      } else if (this.bookingIsNotVetting && this.bookingTypeIsBookingRequest && this.bookingHaveNoAppliedTalent) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the talent reviewing request progress step.
     *
     * @return string
     */
    iconClassTalentReviewingRequest () {
      if (this.bookingIsVetting || this.bookingHaveNoAppliedTalent) {
        return 'fa-circle-o'
      }

      return 'fa-check-circle'
    },

    /**
     * Alert class for the talent chosen progress step.
     *
     * @return string
     */
    alertClassTalentChosen () {
      if (this.bookingHasAllSelectedTalent) {
        return 'alert-success'
      } else if (this.bookingIsVetting || (this.bookingTypeIsBookingRequest && this.bookingHaveNoAppliedTalent)) {
        return 'alert-default'
      } else if (this.bookingTypeIsBookingRequest && this.bookingHaveAppliedTalent) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the talent chosen progress step.
     *
     * @return string
     */
    iconClassTalentChosen () {
      if (this.bookingHasAllSelectedTalent) {
        return 'fa-check-circle'
      }
      return 'fa-circle-o'
    },

    /**
     * Alert class for the terms and conditions and deposit paid progress step.
     *
     * @return string
     */
    alertClassTermsAcceptedAndDepositPaid () {
      if (this.bookingHasAllSelectedTalent && this.bookingPaymentDepositIsUnpaid) {
        return 'alert-info'
      } else if (this.bookingIsVetting || this.bookingTypeIsBookingRequest) {
        return 'alert-default'
      } else if (this.bookingTermsAreNotAccepted || this.bookingPaymentDepositIsUnpaid) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Alert class for the booking status shown to admin users
     *
     * @return string
     */
    alertClassBookingStatus () {
      if (this.bookingIsVetting) {
        return 'alert-info'
      } else if (this.bookingPaymentDepositIsUnpaid || this.bookingPaymentFinalIsUnpaid) {
        return 'alert-danger'
      }

      return 'alert-info'
    },

    /**
     * Icon class for the terms and conditions and deposit paid progress step.
     *
     * @return string
     */
    iconClassTermsAcceptedAndDepositPaid () {
      if (this.bookingIsVetting || this.bookingTypeIsBookingRequest || this.bookingTermsAreNotAccepted || this.bookingPaymentDepositIsUnpaid) {
        return 'fa-info-o'
      }

      return 'fa-check-circle'
    },

    /**
     * Alert class for the final payment progress step.
     *
     * @return string
     */
    alertClassFinalPaymentPaid () {
      if (this.bookingTypeIsBooking && this.bookingPaymentDepositIsPaid && this.bookingPaymentFinalIsPaid) {
        return 'alert-success'
      } else if (this.bookingTypeIsBooking && this.bookingPaymentDepositIsPaid && this.bookingPaymentFinalIsUnpaid) {
        return 'alert-info'
      }

      return 'alert-danger'
    },

    /**
     * Icon class for the final payment progress step.
     *
     * @return string
     */
    iconClassFinalPaymentPaid () {
      if (this.bookingIsVetting || this.bookingTypeIsBookingRequest || this.bookingPaymentFinalIsUnpaid) {
        return 'fa-check-circle'
      }

      return 'fa-check-circle'
    }
  }
}
</script>
